import {
  ListWhatsAppInboxChatsQuerySchema,
  ListWhatsAppInboxMessagesQuerySchema,
  EditWhatsAppInboxMessageBodySchema,
  SendWhatsAppInboxMessageBodySchema,
  SendWhatsAppInboxMessageResponseSchema,
  WhatsAppInboxChatsResponseSchema,
  WhatsAppInboxMessagesResponseSchema,
  type ListWhatsAppInboxChatsQuery,
  type ListWhatsAppInboxMessagesQuery,
  type SendWhatsAppInboxMessageBody,
  type SendWhatsAppInboxMessageResponse,
  type WhatsAppInboxChatsResponse,
  type WhatsAppInboxMessagesResponse,
} from '@zrsystem/contracts';

import { getHttpClient } from '@/shared/api/http-client.js';
import { isHttpError } from '@/shared/api/is-http-error.js';

export function whatsappInboxApiPath(placeId: string, suffix = ''): string {
  return `/api/places/${placeId}/whatsapp-inbox${suffix}`;
}

export function isMissingWhatsAppAccount(error: unknown): boolean {
  return (
    isHttpError(error) &&
    (error.status === 404 || error.code === 'NOTIFICATION_WHATSAPP_ACCOUNT_NOT_FOUND')
  );
}

export async function listWhatsAppInboxChats(
  placeId: string,
  query: ListWhatsAppInboxChatsQuery = {},
): Promise<WhatsAppInboxChatsResponse> {
  const parsed = ListWhatsAppInboxChatsQuerySchema.parse(query);
  const params = new URLSearchParams();
  if (parsed.limit !== undefined) {
    params.set('limit', String(parsed.limit));
  }
  if (parsed.offset !== undefined) {
    params.set('offset', String(parsed.offset));
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : '';

  const raw = await getHttpClient().get<unknown>(
    `${whatsappInboxApiPath(placeId, '/chats')}${suffix}`,
  );
  return WhatsAppInboxChatsResponseSchema.parse(raw);
}

export async function listWhatsAppInboxMessages(
  placeId: string,
  chatId: string,
  query: ListWhatsAppInboxMessagesQuery = {},
): Promise<WhatsAppInboxMessagesResponse> {
  const parsed = ListWhatsAppInboxMessagesQuerySchema.parse(query);
  const params = new URLSearchParams();
  if (parsed.limit !== undefined) {
    params.set('limit', String(parsed.limit));
  }
  if (parsed.afterTimestamp !== undefined) {
    params.set('afterTimestamp', String(parsed.afterTimestamp));
  }
  if (parsed.downloadMedia !== undefined) {
    params.set('downloadMedia', String(parsed.downloadMedia));
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : '';

  try {
    const raw = await getHttpClient().get<unknown>(
      `${whatsappInboxApiPath(placeId, `/chats/${encodeURIComponent(chatId)}/messages`)}${suffix}`,
    );
    return WhatsAppInboxMessagesResponseSchema.parse(raw);
  } catch (error) {
    if (isMissingWhatsAppAccount(error)) {
      return { messages: [] };
    }
    throw error;
  }
}

export async function sendWhatsAppInboxMessage(
  placeId: string,
  chatId: string,
  body: SendWhatsAppInboxMessageBody,
): Promise<SendWhatsAppInboxMessageResponse> {
  const payload = SendWhatsAppInboxMessageBodySchema.parse(body);
  const raw = await getHttpClient().post<unknown>(
    whatsappInboxApiPath(placeId, `/chats/${encodeURIComponent(chatId)}/messages`),
    payload,
  );
  return SendWhatsAppInboxMessageResponseSchema.parse(raw);
}

export async function editWhatsAppInboxMessage(
  placeId: string,
  chatId: string,
  messageId: string,
  text: string,
): Promise<void> {
  const payload = EditWhatsAppInboxMessageBodySchema.parse({ text });
  await getHttpClient().put(
    whatsappInboxApiPath(
      placeId,
      `/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}`,
    ),
    payload,
  );
}

export async function deleteWhatsAppInboxMessageForEveryone(
  placeId: string,
  chatId: string,
  messageId: string,
): Promise<void> {
  await getHttpClient().delete(
    whatsappInboxApiPath(
      placeId,
      `/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}`,
    ),
  );
}
